import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { onboardingAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, FileText, PencilSimpleLine, Confetti, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import PdfFormFiller from '../components/onboarding/PdfFormFiller';
import DocumentUploadList from '../components/onboarding/DocumentUploadList';

export default function OnboardingPublicPage() {
  const { token } = useParams();
  const [packet, setPacket] = useState(null);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const res = await onboardingAPI.publicPacket(token);
      setPacket(res.data);
    } catch (e) { setError(e.response?.data?.detail || 'This link is invalid or has expired'); }
  };
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      await onboardingAPI.publicSubmit(token, active.template_id, values);
      toast.success(`${active.name} submitted — thank you!`);
      setActive(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Submission failed, please try again'); }
    finally { setSubmitting(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="rounded-2xl max-w-md w-full"><CardContent className="p-8 text-center">
          <WarningCircle weight="duotone" className="w-14 h-14 text-destructive mx-auto mb-3" />
          <h1 className="font-heading text-xl font-semibold">Link not valid</h1>
          <p className="text-sm text-muted-foreground mt-2">{error}</p>
        </CardContent></Card>
      </div>
    );
  }
  if (!packet) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  if (active) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <PdfFormFiller form={active} file={onboardingAPI.publicPdfUrl(token, active.template_id)} onSubmit={handleSubmit} onBack={() => setActive(null)} submitting={submitting} />
      </div>
    );
  }

  const done = packet.forms.filter(f => f.status === 'completed').length;
  const reqDocs = packet.documents.filter(d => d.required);
  const reqDone = reqDocs.filter(d => d.status === 'uploaded').length;
  const totalItems = packet.forms.length + reqDocs.length;
  const doneItems = done + reqDone;
  const allDone = totalItems > 0 && doneItems === totalItems;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="onboarding-public-page">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-primary">{packet.company_name} · New Hire Onboarding</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold mt-1">Welcome, {packet.first_name}!</h1>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base">Please complete and sign each form{reqDocs.length ? ' and upload the requested documents' : ''} below. Your progress is saved after every step, so you can come back to this link any time.</p>
        </div>

        <div className="rounded-2xl bg-white border border-border p-4 flex items-center gap-4" data-testid="onboarding-progress">
          <div className="flex-1">
            <div className="flex justify-between text-sm mb-1.5"><span className="font-medium">{done} of {packet.forms.length} forms{reqDocs.length ? ` · ${reqDone} of ${reqDocs.length} documents` : ''}</span><span className="text-muted-foreground tabular-nums">{totalItems ? Math.round((doneItems / totalItems) * 100) : 0}%</span></div>
            <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary transition-all duration-500" style={{ width: `${totalItems ? (doneItems / totalItems) * 100 : 0}%` }} /></div>
          </div>
        </div>

        {allDone && (
          <Card className="rounded-2xl border-emerald-200 bg-emerald-50" data-testid="all-done-card"><CardContent className="p-6 flex items-start gap-4">
            <Confetti weight="duotone" className="w-10 h-10 text-emerald-600 shrink-0" />
            <div><h2 className="font-heading text-lg font-semibold text-emerald-900">You're all set!</h2><p className="text-sm text-emerald-800 mt-1">All your onboarding paperwork has been submitted. {packet.company_name} has been notified and will be in touch about next steps.</p></div>
          </CardContent></Card>
        )}

        {packet.forms.length === 0 && packet.documents.length === 0 && <Card className="rounded-2xl"><CardContent className="p-8 text-center text-muted-foreground text-sm">No forms have been assigned yet. Please check back soon.</CardContent></Card>}

        {packet.forms.length > 0 && <h2 className="font-heading text-lg font-semibold">Forms to sign</h2>}
        <div className="space-y-3">
          {packet.forms.map((f, i) => {
            const completed = f.status === 'completed';
            return (
              <Card key={f.template_id} className={`rounded-2xl transition-shadow ${completed ? 'bg-white/70' : 'hover:shadow-md'}`} data-testid={`packet-form-${f.template_id}`}>
                <CardContent className="p-4 sm:p-5 flex items-center gap-4">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${completed ? 'bg-emerald-100 text-emerald-700' : 'bg-primary/10 text-primary'}`}>
                    {completed ? <CheckCircle weight="fill" className="w-6 h-6" /> : <FileText weight="duotone" className="w-6 h-6" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{i + 1}. {f.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {completed ? `Signed ${f.submitted_at ? format(new Date(f.submitted_at), 'MMM d, yyyy h:mm a') : ''}` : `${f.page_count} page${f.page_count !== 1 ? 's' : ''} · ${f.fields.filter(x => x.required).length} required fields`}
                    </p>
                  </div>
                  {completed ? (
                    <Button variant="outline" size="sm" className="rounded-xl" asChild><a href={onboardingAPI.publicPdfUrl(token, f.template_id)} target="_blank" rel="noreferrer" data-testid={`view-signed-${f.template_id}`}>View</a></Button>
                  ) : (
                    <Button size="sm" className="rounded-xl gap-1" onClick={() => setActive(f)} data-testid={`fill-form-${f.template_id}`}><PencilSimpleLine weight="bold" className="w-4 h-4" /> Fill & Sign</Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
        <DocumentUploadList token={token} documents={packet.documents} onUploaded={load} />
        {packet.hired && <Badge className="rounded-full bg-emerald-100 text-emerald-800">Hired — welcome to the team!</Badge>}
      </div>
    </div>
  );
}
